/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps, ICommandBarStyleProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { IDropdownProps, IDropdownStyles } from '@fluentui/react/lib/Dropdown';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { noop } from 'src/core/utils/base';
import { FileView, FilesViewDropdown } from 'src/customText/appShell/dataTaggingPage/components/FilesViewDropdown';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';

export type ViewOptions = 'labeledFiles' | 'unLabeledFiles' | 'allFiles';

export type SamplingCommandBarProps = {
    documentId: string;
    isLoading?: boolean;
    isInEmptyState: boolean;
    documentCulture?: string;
    onAcceptAllAutoTags?: () => void;
    onRejectAllAutoTags?: () => void;
    isCultureDropdownEnabled?: boolean;
    areAutoTagsControlsHidden: boolean;
    areAutoTagsControlsDisabled: boolean;
    onChangeDocumentCulture?: (culture: string) => void;
    onChangeViewingOption: (options: ViewOptions | FileView) => void;
};

const messages = defineMessages({
    language: { id: 'customText.samplingCommandBar.language', defaultMessage: 'Language:' },
    documentName: { id: 'customText.samplingCommandBar.documentName', defaultMessage: 'Document name' },
    cultureDropdownAriaLabel: {
        id: 'customText.samplingCommandBar.cultureDropdownAriaLabel',
        defaultMessage: 'Language'
    },
    acceptAll: {
        id: 'customText.samplingCommandBar.acceptAll',
        defaultMessage: 'Accept all'
    },
    rejectAll: {
        id: 'customText.samplingCommandBar.rejectAll',
        defaultMessage: 'Reject all'
    },
    acceptOrRejectAllAutotagsTitle: {
        id: 'customText.samplingCommandBar.acceptOrRejectAllAutotagsTitle',
        defaultMessage: 'Accept or reject all suggestions'
    },
    acceptOrRejectAllAutotagsBody: {
        id: 'customText.samplingCommandBar.acceptOrRejectAllAutotagsBody',
        defaultMessage:
            'If you’re happy with all the auto-label suggestions for a file, you can accept them all at once. Or if you don’t like them, you can reject all the suggestions.'
    },
    disabledButtonBody: {
        id: 'customText.samplingCommandBar.disabledButtonBody',
        defaultMessage: 'These buttons are used to accept or reject auto-labels after a successful auto-labeling job'
    }
});

const { getClassName } = getStylist('SamplingCommandBar');
const primarySetClassName = getClassName('commandBarPrimarySet', { alignItems: 'center' });
const commandBarStyles = classNamesFunction<ICommandBarStyleProps, ICommandBarStyles>()({
    primarySet: {
        ' > div': {
            flexShrink: 1
        }
    },
    secondarySet: {
        ' > div': {
            flexShrink: 1
        }
    }
});
const commandBarRootClassName = getClassName('commandBarRoot', theme => ({
    padding: '0px 0px',
    borderStyle: 'solid',
    borderWidth: '1px 0px',
    borderColor: theme.borderColor,
    backgroundColor: theme.backgroundColor
}));

const labelStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { fontSize: '14px', paddingLeft: '12px', paddingRight: '8px' } });
const languageSelectorDropdownStyles = classNamesFunction<IDropdownProps, IDropdownStyles>()({
    title: { borderStyle: 'none', minWidth: '100px', marginTop: '3px' }
});

/**
 * Command bar used for navigating through the documents,
 * change sampling technique and display the "save" status.
 */
export const SamplingCommandBar = observer((props: SamplingCommandBarProps) => {
    const {
        isLoading,
        documentId,
        isInEmptyState,
        documentCulture,
        onAcceptAllAutoTags,
        onRejectAllAutoTags,
        onChangeViewingOption,
        onChangeDocumentCulture = noop,
        isCultureDropdownEnabled = false,
        areAutoTagsControlsHidden = true,
        areAutoTagsControlsDisabled = true
    } = props;
    const { localize } = useLocalization();
    const { uiStore, rootStore } = useGlobalState();

    const viewingDropdownIdRef = React.useRef<string>(getId());

    const { customTextCultureStorehouse, currentCustomTextProjectStore } = rootStore;
    const isHealthcareProject = currentCustomTextProjectStore?.projectKind === 'CustomHealthcare';
    const isCollapsed = uiStore.isCustomTextFilesViewerCollapsed;

    const cultureOptions = isHealthcareProject
        ? customTextCultureStorehouse.items
              .filter(culture => (culture.isoCode === 'he' ? false : culture.isoCode))
              .map(culture => ({
                  key: culture.code,
                  text: culture.name,
                  selected: culture.code === documentCulture
              }))
        : customTextCultureStorehouse.items.map(culture => ({
              key: culture.code,
              text: culture.name,
              selected: culture.code === documentCulture
          }));

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextViewingDropDown'), []);
    React.useEffect(() => () => dequeueTeachingBubble('CustomTextAcceptRejectAllAutoTag'), []);

    React.useEffect(() => {
        if (uiStore.teachingBubbles.CustomTextAcceptRejectSingleAutoTag) {
            createTeachingBubble(
                'CustomTextAcceptRejectAllAutoTag',
                localize(messages.acceptOrRejectAllAutotagsBody),
                localize(messages.acceptOrRejectAllAutotagsTitle),
                `#acceptAllAutotags`,
                true,
                false
            );
        }
    }, [Object.values(uiStore.teachingBubbles)]);

    React.useEffect(() => {
        if (
            !isInEmptyState &&
            !uiStore.teachingBubbles.CustomTextViewingDropDown &&
            (isCollapsed ||
                (uiStore.teachingBubbles.CustomTextFilesViewerSearchButton &&
                    !uiStore.skippedTeachingBubbles.CustomTextFilesViewerSearchButton))
        ) {
            createTeachingBubble(
                'CustomTextViewingDropDown',
                localize(customTextTeachingBubbleMessages.viewingDropDownBody),
                localize(customTextTeachingBubbleMessages.viewingDropDownTitle),
                `#${viewingDropdownIdRef.current}`,
                true,
                true
            );
        }
    }, [isInEmptyState, isCollapsed, Object.values(uiStore.teachingBubbles)]);

    const onRenderCultureDropdown = () => (
        <Stack role="menuitem" horizontal verticalAlign="center">
            <LuisText styles={labelStyles} fontWeight={600} tabIndex={-1}>
                {localize(messages.language)}
            </LuisText>

            <LuisDropdown
                key={documentCulture}
                styles={languageSelectorDropdownStyles}
                ariaLabel={localize(messages.cultureDropdownAriaLabel)}
                disabled={isLoading || customTextCultureStorehouse.tracker.isRunning}
                onChange={(_, option) => onChangeDocumentCulture(option.key as string)}
                options={cultureOptions}
            />
        </Stack>
    );

    const items: ICommandBarItemProps[] = [
        {
            key: 'viewing',
            onRender: () => (
                <Stack role="menuitem" id={viewingDropdownIdRef.current}>
                    <FilesViewDropdown selectedViewingOption="single" onFileViewChange={onChangeViewingOption} />
                </Stack>
            )
        },
        {
            key: 'documentId',
            onRender: () => (
                <LuisText role="menuitem" size="14px" style={{ display: 'flex', flexDirection: 'row' }}>
                    {localize(messages.documentName)}
                    :&nbsp;
                    {isLoading ? (
                        <Shimmer
                            styles={{ root: { marginTop: '2px', marginLeft: '3px' } }}
                            shimmerElements={[{ type: ShimmerElementType.line, width: '70px', height: 18 }]}
                        />
                    ) : (
                        documentId
                    )}
                </LuisText>
            )
        } as ICommandBarItemProps
    ].concat(isCultureDropdownEnabled ? [{ key: 'cultureDropdown', onRender: onRenderCultureDropdown }] : []);

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'acceptAll',
            onRender: (item: ICommandBarItemProps) => (
                <LuisTooltip
                    data-automation-id="rejectAll"
                    hidden={!areAutoTagsControlsDisabled}
                    content={localize(messages.disabledButtonBody)}>
                    <CommandBarButton
                        id="acceptAllAutotags"
                        onClick={onAcceptAllAutoTags}
                        text={localize(messages.acceptAll)}
                        disabled={areAutoTagsControlsDisabled}
                        iconProps={{ iconName: 'CheckList' }}
                        styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                    />
                </LuisTooltip>
            )
        },
        {
            key: 'rejectAll',
            onRender: (item: ICommandBarItemProps) => (
                <LuisTooltip
                    data-automation-id="rejectAll"
                    hidden={!areAutoTagsControlsDisabled}
                    content={localize(messages.disabledButtonBody)}>
                    <CommandBarButton
                        id="rejectAllAutotags"
                        onClick={onRejectAllAutoTags}
                        text={localize(messages.rejectAll)}
                        disabled={areAutoTagsControlsDisabled}
                        iconProps={{ iconName: 'RemoveFromShoppingList' }}
                        styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                    />
                </LuisTooltip>
            )
        }
    ];

    return (
        <LuisCommandBar
            items={items}
            farItems={areAutoTagsControlsHidden ? [] : farItems}
            styles={mergeStyleSets(commandBarStyles, { root: commandBarRootClassName, primarySet: primarySetClassName })}
        />
    );
});
